import React, { useState } from "react";
import PageHero from "../components/PageHero";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { Mail, MapPin, Phone, MessageCircle } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";
import { useSettings } from "../settings";
import SEO from "../components/SEO";
import { waLink } from "../components/WhatsAppFloat";

const Iletisim = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const { t } = useLang();
  const { settings } = useSettings();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: t("common.required") });
      return;
    }
    setLoading(true);
    try {
      await api.submitContact(form);
      toast({ title: t("common.submitOk"), description: t("common.submitDesc") });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Bir hata oluştu" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-white">
      <SEO title={t("contact.title")} description={t("contact.subtitle")} path="/iletisim" />
      <PageHero kicker={t("contact.kicker")} title={t("contact.title")} subtitle={t("contact.subtitle")} />

      <section className="pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 grid lg:grid-cols-5 gap-6 md:gap-10">
          <div className="lg:col-span-2 space-y-6">
            {[
              { icon: MapPin, label: t("contact.address"), value: settings.address },
              { icon: Phone, label: t("contact.phone"), value: settings.phone, href: `tel:${(settings.phone || "").replace(/\s+/g, "")}` },
              { icon: Mail, label: t("contact.email"), value: settings.email, href: `mailto:${settings.email}` },
            ].map((item) => (
              <a
                key={item.label}
                href={item.href || "#"}
                className="flex items-start gap-4 p-6 rounded-2xl border border-neutral-100 hover:border-[#C9A24B] transition-colors"
              >
                <div className="w-11 h-11 rounded-full bg-[#C9A24B]/15 flex items-center justify-center text-[#8F6E1E] shrink-0">
                  <item.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">{item.label}</div>
                  <div className="mt-1 text-[#1a1a1a] font-medium">{item.value}</div>
                </div>
              </a>
            ))}

            {settings.whatsapp && (
              <a
                href={waLink(settings.whatsapp, "Merhaba, web sitenizden yazıyorum.")}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-4 p-6 rounded-2xl bg-[#25D366] text-white hover:bg-[#1ebe5b] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-white/85 font-semibold">WhatsApp</div>
                    <div className="mt-1 font-semibold">{settings.whatsapp}</div>
                  </div>
                </div>
                <span className="hidden sm:inline text-sm font-semibold">Mesaj Gönder →</span>
              </a>
            )}

            <div className="rounded-2xl overflow-hidden border border-neutral-100 aspect-[4/3] bg-neutral-100">
              <iframe
                title="Koptur Grup Konum"
                src={settings.map_embed_url}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-3 bg-neutral-50 rounded-2xl p-6 md:p-10 border border-neutral-100"
          >
            <h3 className="text-2xl md:text-3xl font-black text-[#1a1a1a]">{t("contact.formTitle")}</h3>
            <p className="mt-2 text-neutral-600">{t("contact.formDesc")}</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-neutral-700">{t("contact.name")} *</Label>
                <Input
                  className="mt-1.5 bg-white h-11"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("contact.namePh")}
                />
              </div>
              <div>
                <Label className="text-sm text-neutral-700">{t("contact.email")} *</Label>
                <Input
                  type="email"
                  className="mt-1.5 bg-white h-11"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t("contact.emailPh")}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm text-neutral-700">{t("contact.phone")}</Label>
                <Input
                  className="mt-1.5 bg-white h-11"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder={t("contact.phonePh")}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm text-neutral-700">{t("contact.message")} *</Label>
                <Textarea
                  rows={5}
                  className="mt-1.5 bg-white"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={t("contact.messagePh")}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="mt-6 bg-[#1a1a1a] hover:bg-[#C9A24B] text-white h-11 px-8 rounded-full text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {loading ? t("common.loading") : t("common.send")}
            </Button>
          </form>
        </div>
      </section>
      <Toaster />
    </main>
  );
};

export default Iletisim;
